"use client"

import { useEffect, useRef, useState } from "react"

/**
 * Which of a set of sections is currently in view, and how to jump to one.
 *
 * There is no scroll-spy anywhere else in this repo, so this is new code rather
 * than a reuse — kept deliberately small.
 *
 * THE TRAP, and it is the whole reason this is a hook rather than four lines
 * inline: **the demo does not scroll the window.** `lernpfad-demo.tsx` scrolls
 * an inner `div.h-full.overflow-y-auto`, part of the `h-full` chain that
 * `charts/chart-theme.tsx` documents as load-bearing. An IntersectionObserver
 * left on its default root observes the viewport and reports every section as
 * visible forever; `scrollIntoView` on the document does nothing at all. Both
 * fail silently and look like the feature simply not working.
 *
 * So the container is located by `closest("[data-lp-scroll]")` from a mounted
 * section — no ref drilling through three components, and it degrades to the
 * viewport if the attribute is ever removed.
 */
/** Breathing room between the sticky bar and a jumped-to heading. */
const JUMP_GAP = 16

export function useScrollSpy(ids: string[]): {
    active: string | undefined
    register: (id: string) => (node: HTMLElement | null) => void
    jumpTo: (id: string) => void
} {
    const nodes = useRef(new Map<string, HTMLElement>())
    const [active, setActive] = useState<string | undefined>(ids[0])
    /**
     * Ignore observer updates until a click-driven smooth scroll has settled.
     *
     * Without it the intermediate frames of the animation each report a
     * different topmost section and the highlight visibly races past the target
     * before landing.
     */
    const lockUntil = useRef(0)
    // A stable dependency: `ids` is a fresh array each render, and putting the
    // expression inline makes it unverifiable for the exhaustive-deps rule.
    const key = ids.join("|")

    useEffect(() => {
        const first = nodes.current.values().next().value
        if (!first) return
        const root = first.closest("[data-lp-scroll]") as HTMLElement | null

        /**
         * Start the detection band BELOW the sticky bar.
         *
         * With a top margin of 0 the band begins at the container's top edge —
         * which is behind the bar, where the PREVIOUS section is still showing.
         * The result is a highlight that sits one section too early at every
         * jump: consistently wrong, and consistently plausible enough to look
         * like a rounding problem rather than a geometry one.
         */
        const barHeight =
            Number.parseInt(
                (root ? getComputedStyle(root) : getComputedStyle(document.documentElement))
                    .getPropertyValue("--lp-bar-height") || "72",
                10,
            ) || 72
        const topInset = barHeight + JUMP_GAP

        const visible = new Set<string>()
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const id = entry.target.getAttribute("data-section") ?? ""
                    if (entry.isIntersecting) visible.add(id)
                    else visible.delete(id)
                }
                // Topmost visible section wins. Resolved from DOM position
                // rather than from the id array, which keeps the observer
                // callback free of any dependency on props — and is the more
                // honest question anyway: "which section is highest on screen"
                // does not care what order they were declared in.
                if (Date.now() < lockUntil.current) return

                /**
                 * At the bottom of the container the topmost-visible rule
                 * breaks down: the last sections can never reach the top of the
                 * band, so they can never win, and clicking them in the rail
                 * appears to do nothing. Once scrolled to the end, the LAST
                 * visible section is the one being read.
                 */
                if (root && root.scrollTop + root.clientHeight >= root.scrollHeight - 8) {
                    let last: { id: string; top: number } | undefined
                    for (const [id, node] of nodes.current) {
                        if (!visible.has(id)) continue
                        const top = node.getBoundingClientRect().top
                        if (!last || top > last.top) last = { id, top }
                    }
                    if (last) setActive(last.id)
                    return
                }

                let best: { id: string; top: number } | undefined
                for (const [id, node] of nodes.current) {
                    if (!visible.has(id)) continue
                    const top = node.getBoundingClientRect().top
                    // Within a few pixels the two are level — side by side in a
                    // multi-column layout, say — and registration order decides,
                    // which follows declaration order.
                    if (!best || top < best.top - 4) best = { id, top }
                }
                if (best) setActive(best.id)
            },
            {
                root,
                // `scroll-reveal.tsx`'s threshold, minus its
                // unobserve-after-first-hit: this has to keep tracking, not fire
                // once. The margins carve out a band that starts just below the
                // sticky bar and ends before the fold, so "in view" means "the
                // reader is actually looking at it".
                threshold: 0,
                rootMargin: `-${topInset}px 0px -55% 0px`,
            },
        )

        for (const node of nodes.current.values()) observer.observe(node)
        return () => observer.disconnect()
        // Re-run when the set of sections changes, not on every render.
    }, [key])

    const register = (id: string) => (node: HTMLElement | null) => {
        if (node) nodes.current.set(id, node)
        else nodes.current.delete(id)
    }

    const jumpTo = (id: string) => {
        const node = nodes.current.get(id)
        if (!node) return
        setActive(id)
        lockUntil.current = Date.now() + 800
        const root = node.closest("[data-lp-scroll]") as HTMLElement | null
        if (!root) {
            node.scrollIntoView({ behavior: "smooth", block: "start" })
            return
        }
        // Offset by the sticky bar so the heading is not hidden behind it.
        const bar =
            Number.parseInt(getComputedStyle(root).getPropertyValue("--lp-bar-height") || "72", 10) ||
            72
        const top =
            node.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop
        root.scrollTo({ top: Math.max(0, top - bar - JUMP_GAP), behavior: "smooth" })
    }

    return { active, register, jumpTo }
}
